import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";

import { MetricPicker } from "./MetricPicker";
import {
  MAX_BONUS_STEPS, MAX_PER_LABEL, PILL_OPPORTUNITY, commissionFits, defaultStepsFor, isStepBonus, rateSuffixOf,
} from "../../../components/CustomWidget/bonusSteps";
import { BEAT_YETI_RATE_ID } from "../../../hooks/useDerivedRevenueMetrics";
import "./customize.css";

/**
 * "Pay a bonus on it" for a Build Your Own card — one control for the create
 * flow and the editor, so the two can never drift apart again.
 *
 * Two ways to pay, and the metric decides which one is honest:
 *
 *   Commission   a percentage of whatever the metric reaches — only for a
 *                dollar TOTAL (revenue, sales)
 *   Steps        clear a line, earn a set amount — for a rate, a per-hour
 *                figure or a team standing (Technicians' Revenue per Hour,
 *                Beat the Yeti rate, Closed Rate…), where a percentage of the
 *                number is not money anyone earned
 *
 * Picking a metric a commission cannot price switches to steps on its own and
 * says why, rather than letting someone build "10% of 72%".
 *
 * The draft keeps both halves (pct AND steps) while it is being edited, so
 * flipping the switch back and forth never loses what was typed; the card and
 * the layout store read it through bonusSteps.sanitizeBonus, which keeps only
 * the half `type` names.
 */

const DEFAULT_LABEL = { pct: "Earned from this", steps: "Team bonus" };
const unitKey = (m) => (m ? `${m.kind || "#"}${rateSuffixOf(m)}` : "");

const X = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden>
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);
const Plus = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden>
    <path d="M12 5v14M5 12h14" />
  </svg>
);

/* One "show this on the card" switch — the same row the Beat the Yeti display
   options use. On = shown; only the off state is stored (`hide*: true`). */
const ShowToggle = ({ shown, title, sub, onClick }) => (
  <button type="button" className={`gmbw-whotog${shown ? " on" : ""}`} onClick={onClick} aria-pressed={shown}>
    <span className="gmbw-whotog-t">
      <b>{title}</b>
      <span>{sub}</span>
    </span>
    <span className="gmbw-whotog-sw"><i /></span>
  </button>
);
ShowToggle.propTypes = {
  shown: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  sub: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
};

/* A bonus metric's picker list: the card's own metrics first, then the rest. */
const optionsFor = (chosen, all) => {
  const picked = (chosen || []).filter(Boolean);
  const ids = new Set(picked.map((m) => m.id));
  return [
    ...picked.map((m) => ({ ...m, group: "On this component" })),
    ...(all || []).filter((m) => m && !ids.has(m.id)).map((m) => ({ ...m, group: "Other company metrics" })),
  ];
};

const BonusField = ({ value, onChange, chosen, catalog, metricOf, labelClassName, showHint }) => {
  const bonus = value || null;
  const boxRef = useRef(null);
  const on = !!bonus;

  /* Switching the bonus on opens a box at the bottom of the panel — bring it
     into view, or the toggle looks like it did nothing. */
  useEffect(() => {
    if (!on || !boxRef.current) return undefined;
    const t = setTimeout(() => boxRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 40);
    return () => clearTimeout(t);
  }, [on]);

  const metric = bonus ? metricOf(bonus.ofMetric) : null;
  const fits = commissionFits(metric);
  const steps = isStepBonus(bonus);
  const mode = steps ? "steps" : "pct";
  const kind = (metric && metric.kind) || "#";
  const suffix = rateSuffixOf(metric);
  const stepRows = bonus && Array.isArray(bonus.steps) && bonus.steps.length ? bonus.steps : [];

  const set = (patch) => onChange({ ...bonus, ...patch });

  /* A default label follows the mode; one somebody typed is theirs. */
  const labelFor = (nextMode) => {
    const cur = bonus ? bonus.label : "";
    const isDefault = !cur || Object.values(DEFAULT_LABEL).includes(cur);
    return isDefault ? DEFAULT_LABEL[nextMode] : cur;
  };

  const switchOn = () => {
    const pick = chosen.find((m) => m && m.kind === "$" && commissionFits(m)) || chosen.find(Boolean) || null;
    if (pick && !commissionFits(pick)) {
      onChange({ type: "steps", label: DEFAULT_LABEL.steps, ofMetric: pick.id, per: "player", pct: 10, steps: defaultStepsFor(pick) });
    } else {
      onChange({ label: DEFAULT_LABEL.pct, pct: 10, ofMetric: pick ? pick.id : null });
    }
  };

  const setMode = (nextMode) => {
    if (nextMode === mode) return;
    if (nextMode === "pct") {
      if (!fits) return;
      const { type, ...rest } = bonus;
      onChange({ ...rest, label: labelFor("pct"), pct: Number(bonus.pct) > 0 ? bonus.pct : 10 });
      return;
    }
    onChange({
      ...bonus,
      type: "steps",
      label: labelFor("steps"),
      per: bonus.per === "team" ? "team" : "player",
      steps: stepRows.length ? stepRows : defaultStepsFor(metric),
    });
  };

  const pickMetric = (m) => {
    const next = { ...bonus, ofMetric: m.id };
    /* Steps typed in $/hr mean nothing on a percentage — start the ladder over
       whenever the unit underneath it changes. */
    const unitChanged = unitKey(m) !== unitKey(metric);
    if (!commissionFits(m) || steps) {
      onChange({
        ...next,
        type: "steps",
        label: labelFor("steps"),
        per: bonus.per === "team" ? "team" : "player",
        steps: !unitChanged && stepRows.length ? stepRows : defaultStepsFor(m),
      });
      return;
    }
    onChange(next);
  };

  const setStep = (i, patch) => set({ steps: stepRows.map((s, x) => (x === i ? { ...s, ...patch } : s)) });
  const addStep = () => {
    const last = stepRows[stepRows.length - 1];
    const lastAt = Number(last && last.at) || 0;
    const lastPay = Number(last && last.pay) || 0;
    let nextAt = "";
    if (lastAt > 0) nextAt = kind === "%" ? Math.min(100, lastAt + 10) : Math.round(lastAt * 1.15);
    set({ steps: [...stepRows, { at: nextAt, pay: lastPay > 0 ? lastPay * 2 : "" }] });
  };
  const dropStep = (i) => set({ steps: stepRows.filter((_, x) => x !== i) });

  const metricName = (metric && (metric.label || metric.title)) || "This metric";
  let pillNote = "Shows the top step on its own — “Up to $350”.";
  if (bonus && bonus.pill === PILL_OPPORTUNITY) {
    pillNote = bonus.per === "team"
      ? "Shows the pot’s top step — “Bonus Opportunity $500”."
      : "Shows the top step times every player it pays — “Bonus Opportunity $2,450” for $350 across 7 players.";
  }
  /* Flip a hide* flag: drop the key when shown so the default stays unstored. */
  const toggleShown = (key) => {
    const { [key]: was, ...rest } = bonus;
    onChange(was === true ? rest : { ...rest, [key]: true });
  };

  return (
    <>
      <div className="gmbw-bonusbar">
        <label className={labelClassName} style={{ margin: 0 }}>Pay a bonus on it</label>
        <button
          type="button"
          role="switch"
          aria-checked={on}
          aria-label="Pay a bonus on it"
          className={`gmbw-switch${on ? " on" : ""}`}
          onClick={() => (on ? onChange(null) : switchOn())}
        >
          <span className="gmbw-switch-dot" />
        </button>
      </div>
      {showHint && (
        <p className="gmbw-fhint" style={{ marginTop: 8 }}>
          Turns a metric into money: a commission on a dollar total, or set
          amounts for clearing lines on a rate like Revenue per Hour or the
          Beat the Yeti rate. The card shows what&rsquo;s being earned right
          next to the number.
        </p>
      )}
      {bonus && (
        <div className="gmbw-bonusbox" ref={boxRef}>
          <label className="gmbw-blabel" htmlFor="gmbw-bonus-label">What to call it on the card</label>
          <input
            id="gmbw-bonus-label"
            className="gmbw-input"
            type="text"
            maxLength={60}
            value={bonus.label || ""}
            placeholder={DEFAULT_LABEL[mode]}
            onChange={(e) => set({ label: e.target.value })}
          />

          <label className="gmbw-blabel">Paid on</label>
          <MetricPicker metrics={optionsFor(chosen, catalog)} value={bonus.ofMetric} onPick={pickMetric} />

          <label className="gmbw-blabel">How it pays</label>
          <div className="gmbw-seg gmbw-bseg" role="radiogroup" aria-label="How the bonus pays">
            <button
              type="button"
              role="radio"
              aria-checked={mode === "pct"}
              className={`gmbw-segbtn${mode === "pct" ? " on" : ""}`}
              disabled={!fits}
              onClick={() => setMode("pct")}
            >
              Commission
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={mode === "steps"}
              className={`gmbw-segbtn${mode === "steps" ? " on" : ""}`}
              onClick={() => setMode("steps")}
            >
              Steps
            </button>
          </div>
          {!fits && (
            <p className="gmbw-bnote">
              {metricName} is a rate, not a dollar total, so it pays in steps: clear a line, earn a set amount.
            </p>
          )}

          {mode === "pct" && (
            <div className="gmbw-bonuseq">
              <input
                className="gmbw-input num"
                type="number"
                min="0"
                max="100"
                step="0.1"
                aria-label="Commission percent"
                value={bonus.pct ?? ""}
                onChange={(e) => set({ pct: e.target.value })}
              />
              <span>% of what {metricName} reaches</span>
            </div>
          )}

          {mode === "steps" && (
            <>
              <div className="gmbw-bsteps">
                {stepRows.map((s, i) => (
                  // eslint-disable-next-line react/no-array-index-key -- rows are positional while typing
                  <div className="gmbw-bstep" key={`bstep${i}`}>
                    <span className="gmbw-bstep-k">At</span>
                    <label className="gmbw-bstep-in">
                      {kind === "$" && <i>$</i>}
                      <input
                        type="number"
                        min="0"
                        step="any"
                        inputMode="decimal"
                        aria-label={`Step ${i + 1} line`}
                        placeholder={kind === "%" ? "70" : "175"}
                        value={s.at ?? ""}
                        onChange={(e) => setStep(i, { at: e.target.value })}
                      />
                      {kind === "%" && <i>%</i>}
                      {suffix && <i>{suffix}</i>}
                    </label>
                    <span className="gmbw-bstep-k">pays</span>
                    <label className="gmbw-bstep-in">
                      <i>$</i>
                      <input
                        type="number"
                        min="0"
                        step="any"
                        inputMode="decimal"
                        aria-label={`Step ${i + 1} pays`}
                        placeholder="250"
                        value={s.pay ?? ""}
                        onChange={(e) => setStep(i, { pay: e.target.value })}
                      />
                    </label>
                    <button
                      type="button"
                      className="gmbw-mrow-x"
                      aria-label={`Remove step ${i + 1}`}
                      disabled={stepRows.length <= 1}
                      onClick={() => dropStep(i)}
                    >
                      <X />
                    </button>
                  </div>
                ))}
              </div>
              {stepRows.length < MAX_BONUS_STEPS ? (
                <button type="button" className="gmbw-addrow" onClick={addStep}>
                  <Plus /> Add a step
                </button>
              ) : (
                <div className="gmbw-addrow full">{MAX_BONUS_STEPS} steps — that’s the max</div>
              )}

              <label className="gmbw-blabel">Who it pays</label>
              <div className="gmbw-seg gmbw-bseg" role="radiogroup" aria-label="Who the bonus pays">
                <button
                  type="button"
                  role="radio"
                  aria-checked={bonus.per !== "team"}
                  className={`gmbw-segbtn${bonus.per !== "team" ? " on" : ""}`}
                  onClick={() => set({ per: "player" })}
                >
                  Each player
                </button>
                <button
                  type="button"
                  role="radio"
                  aria-checked={bonus.per === "team"}
                  className={`gmbw-segbtn${bonus.per === "team" ? " on" : ""}`}
                  onClick={() => set({ per: "team" })}
                >
                  One team pot
                </button>
              </div>
              <label className="gmbw-blabel" htmlFor="gmbw-bonus-per">Under each amount</label>
              <input
                id="gmbw-bonus-per"
                className="gmbw-input"
                type="text"
                maxLength={MAX_PER_LABEL}
                value={bonus.perLabel || ""}
                placeholder={bonus.per === "team" ? "Nothing" : "Per player"}
                onChange={(e) => set({ perLabel: e.target.value })}
              />
              <p className="gmbw-bnote">
                A short caption in each step box so nobody reads the amount as the total — “Per Tech”, “Per player”.
              </p>
              <p className="gmbw-bnote">
                {bonus.ofMetric === BEAT_YETI_RATE_ID
                  ? "Lines are the share of scored players ahead of their own team’s Yeti — 70 means 70% of them. The card counts how many more players each step needs."
                  : "The highest line reached pays. Steps sort themselves low to high."}
              </p>
            </>
          )}

          <label className="gmbw-blabel">On the card</label>
          <div className="gmbw-bshow">
            <ShowToggle
              shown={bonus.hidePill !== true}
              title="Header pill"
              sub={steps ? "“Up to $500” next to the card’s title." : "“10% commission” next to the card’s title."}
              onClick={() => toggleShown("hidePill")}
            />
            {steps && bonus.hidePill !== true && (
              <div className="gmbw-seg gmbw-bseg" role="radiogroup" aria-label="What the header pill says">
                <button
                  type="button"
                  role="radio"
                  aria-checked={bonus.pill !== PILL_OPPORTUNITY}
                  className={`gmbw-segbtn${bonus.pill !== PILL_OPPORTUNITY ? " on" : ""}`}
                  onClick={() => { const { pill, ...rest } = bonus; onChange(rest); }}
                >
                  Up to
                </button>
                <button
                  type="button"
                  role="radio"
                  aria-checked={bonus.pill === PILL_OPPORTUNITY}
                  className={`gmbw-segbtn${bonus.pill === PILL_OPPORTUNITY ? " on" : ""}`}
                  onClick={() => set({ pill: PILL_OPPORTUNITY })}
                >
                  Bonus Opportunity
                </button>
              </div>
            )}
            {steps && bonus.hidePill !== true && (
              <p className="gmbw-bnote">
{pillNote}
              </p>
            )}
            {steps && (
              <>
                <ShowToggle
                  shown={bonus.hideSteps !== true}
                  title="Step boxes"
                  sub="Each line and what it pays, under the bar."
                  onClick={() => toggleShown("hideSteps")}
                />
                <ShowToggle
                  shown={bonus.hideNext !== true}
                  title="What’s next"
                  sub="The line saying what unlocks the next step."
                  onClick={() => toggleShown("hideNext")}
                />
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

BonusField.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  value: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  chosen: PropTypes.arrayOf(PropTypes.object),
  // eslint-disable-next-line react/forbid-prop-types
  catalog: PropTypes.arrayOf(PropTypes.object),
  metricOf: PropTypes.func.isRequired,
  labelClassName: PropTypes.string,
  showHint: PropTypes.bool,
};
BonusField.defaultProps = {
  value: null, chosen: [], catalog: [], labelClassName: undefined, showHint: false,
};

export default BonusField;
